import { afterNextRender, Component, DestroyRef, ElementRef, inject, output, signal, viewChild } from '@angular/core';
import { IonButton, IonIcon } from '@ionic/angular';
import { TranslationPipe } from '@raptor-fe/services';
import SignaturePad from 'signature_pad';

/**
 * Ink used for the exported PNG. The pad draws in the theme's text colour so the stroke is visible
 * while signing, but the stored image is viewed later on a light surface (web inspection view), so a
 * dark-mode signature exported as drawn would be white ink on a transparent background and vanish.
 */
const EXPORT_INK = 'rgb(0, 0, 0)';

/** Stroke widths shared by the on-screen pad and the export redraw so the PNG matches what was signed. */
const STROKE_OPTIONS = { minWidth: 0.5, maxWidth: 2.5 } as const;

/**
 * Touch signature capture for mobile flows. Hosts a `signature_pad` canvas with a "Sign here" overlay
 * and a Clear button; the parent reads {@link hasSignature} to gate its confirm action and calls
 * {@link toBlob} to obtain the PNG to upload. Parents that must react to every change, not only the
 * signed/unsigned flip, listen to {@link signatureChange}.
 *
 * Shared by the check-in terms step and the technician signature sheet. The canvas is sized from its laid-out box after render and
 * again whenever that box changes, because inside an ion-modal sheet the first measurement can happen
 * before the sheet has animated to its breakpoint. Resizing a canvas wipes it, so existing strokes are
 * replayed after every resize.
 *
 * The canvas stops touchstart and mousedown from bubbling. An ion-modal sheet starts its drag-to-dismiss
 * gesture from those events unless the touch began inside ion-content, so without this a stroke on a pad
 * hosted outside ion-content drags the sheet while signing. Drawing is unaffected because signature_pad
 * listens on the canvas itself.
 */
@Component({
    selector: 'lib-signature-pad',
    imports: [IonButton, IonIcon, TranslationPipe],
    template: `
        <div class="border-2 rounded-lg relative mb-3">
            <canvas
                #signatureCanvas
                class="w-full block"
                style="height: 200px; touch-action: none"
                data-testid="signature-canvas"
                (touchstart)="$event.stopPropagation()"
                (mousedown)="$event.stopPropagation()"
            ></canvas>

            @if (!hasSignature()) {
                <div class="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="signature-placeholder">
                    <span>{{ { feature: 'Common', component: 'Signature Pad', label: 'Sign here' } | translate }}</span>
                </div>
            }
        </div>

        <ion-button (click)="clear()" [disabled]="!hasSignature()" fill="clear" data-testid="signature-clear">
            <ion-icon name="refresh-outline" slot="start"></ion-icon>
            {{ { feature: 'Common', component: 'Signature Pad', label: 'Clear Signature' } | translate }}
        </ion-button>
    `,
})
export class SignaturePadComponent {
    private readonly destroyRef = inject(DestroyRef);

    private readonly canvasElement = viewChild.required<ElementRef<HTMLCanvasElement>>('signatureCanvas');

    private signaturePad: SignaturePad | null = null;

    private resizeObserver: ResizeObserver | null = null;

    private readonly signed = signal(false);

    /** True once at least one stroke has been drawn and not cleared. */
    readonly hasSignature = this.signed.asReadonly();

    /**
     * Emits {@link hasSignature} after every completed stroke and every clear. A stroke added to an
     * existing signature leaves {@link hasSignature} unchanged, so a parent that versions the drawn image
     * (to tell a retry with the same signature from a re-sign) needs this event rather than the signal.
     */
    readonly signatureChange = output<boolean>();

    constructor() {
        afterNextRender(() => this.initialise());
        this.destroyRef.onDestroy(() => {
            this.resizeObserver?.disconnect();
            this.signaturePad?.off();
        });
    }

    /** Wipes the canvas and resets {@link hasSignature}. */
    clear(): void {
        this.signaturePad?.clear();
        this.signed.set(false);
        this.signatureChange.emit(false);
    }

    /**
     * Exports the signature as a PNG drawn in {@link EXPORT_INK} on a transparent background.
     *
     * @returns The PNG blob.
     * @throws When nothing has been signed or the canvas could not be encoded.
     */
    toBlob(): Promise<Blob> {
        const pad = this.signaturePad;
        const canvas = this.canvasElement().nativeElement;
        if (pad == null || pad.isEmpty()) {
            return Promise.reject(new Error('No signature to export.'));
        }

        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = canvas.width;
        exportCanvas.height = canvas.height;
        const exporter = new SignaturePad(exportCanvas, { ...STROKE_OPTIONS, penColor: EXPORT_INK });
        exporter.fromData(pad.toData().map((group) => ({ ...group, penColor: EXPORT_INK })));
        exporter.off();

        return new Promise<Blob>((resolve, reject) => {
            exportCanvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Signature could not be encoded.'))), 'image/png');
        });
    }

    /**
     * Creates the pad once the canvas has a laid-out size and starts tracking size changes.
     */
    private initialise(): void {
        const canvas = this.canvasElement().nativeElement;
        this.sizeCanvas(canvas);

        this.signaturePad = new SignaturePad(canvas, { ...STROKE_OPTIONS, penColor: this.inkColor() });
        this.signaturePad.addEventListener('endStroke', () => {
            const signed = !this.signaturePad?.isEmpty();
            this.signed.set(signed);
            this.signatureChange.emit(signed);
        });

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.onResize(canvas));
            this.resizeObserver.observe(canvas);
        }
    }

    /**
     * Re-sizes the drawing buffer to the canvas box and replays the strokes the resize wiped. Ignores
     * notifications that do not change the size, which ResizeObserver delivers on first observe.
     */
    private onResize(canvas: HTMLCanvasElement): void {
        const pad = this.signaturePad;
        if (pad == null) {
            return;
        }
        const { width, height } = this.measure(canvas);
        if (width === canvas.width && height === canvas.height) {
            return;
        }
        const strokes = pad.toData();
        this.sizeCanvas(canvas);
        pad.clear();
        if (strokes.length > 0) {
            pad.fromData(strokes);
        }
    }

    /** Matches the drawing buffer to the laid-out box, with fallbacks for an unlaid-out canvas. */
    private sizeCanvas(canvas: HTMLCanvasElement): void {
        const { width, height } = this.measure(canvas);
        canvas.width = width;
        canvas.height = height;
    }

    private measure(canvas: HTMLCanvasElement): { width: number; height: number } {
        return {
            width: canvas.offsetWidth || canvas.clientWidth || 400,
            height: canvas.offsetHeight || canvas.clientHeight || 200,
        };
    }

    /**
     * The ink shown while signing. Read from the theme rather than fixed black, since the pad sits on the
     * page's own background and black ink disappears on a dark one.
     */
    private inkColor(): string {
        const resolved = getComputedStyle(document.documentElement).getPropertyValue('--ion-text-color').trim();
        return resolved || EXPORT_INK;
    }
}
